import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { http, formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Phone, Target, Users, CheckCircle2, Bell, Flame, ShieldOff, PlayCircle, BarChart3, GraduationCap, ArrowRight } from 'lucide-react';

function Stat({ icon: Icon, label, value, hint, tint }) {
  return (
    <div className="hq-card p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="hq-eyebrow">{label}</span>
        <Icon size={16} style={{ color: tint || 'var(--plum)' }} strokeWidth={1.4} />
      </div>
      <div className="font-serif text-3xl font-light" style={{ color: 'var(--sage)' }} data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
        {value ?? '—'}
      </div>
      {hint && <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{hint}</div>}
    </div>
  );
}

export default function CallCommand() {
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [s, l] = await Promise.all([
          http.get('/call/summary'),
          http.get('/call/leads'),
        ]);
        setSummary(s.data);
        setLeads(l.data);
      } catch (e) { toast.error(formatError(e)); }
    })();
  }, []);

  const startNextCall = async () => {
    const next = leads.find(l => !l.do_not_call);
    if (!next) return toast.info('Add or import a lead first.');
    try {
      const { data } = await http.post('/call/sessions', { lead_id: next.id });
      nav(`/call/session/${data.session.id}`);
    } catch (e) { toast.error(formatError(e)); }
  };

  const goal = summary?.daily_goal || 25;
  const done = summary?.sessions_today || 0;
  const pct = Math.min(100, Math.round((done / goal) * 100));

  return (
    <div className="space-y-8">
      <header className="hq-fade-in">
        <p className="hq-plum-eyebrow">Homi Call Command</p>
        <h1 className="hq-h1 hq-page-title mt-3" data-testid="call-command-title">More Conversations. Less Scrambling.</h1>
        <div className="hq-divider-gold w-fit"></div>
        <p className="mt-4 max-w-2xl text-sm" style={{ color: 'var(--ink-soft)', lineHeight: 1.75 }}>
          Homi isn't a robot pretending to be a realtor. It's the command center behind the realtor —
          preparing every call, guiding the conversation, and handling the follow-up.
        </p>
      </header>

      {/* Daily goal + Start button */}
      <div className="hq-card p-8 hq-fade-in-delay-1" style={{ background: 'linear-gradient(135deg, #FBF6EC 0%, var(--plum-tint) 100%)', border: '1px solid var(--plum-soft)' }}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <div className="flex-1">
            <div className="hq-plum-eyebrow">Today's Session</div>
            <div className="font-serif text-2xl md:text-3xl mt-2" style={{ color: 'var(--sage)' }}>
              {done} / {goal} calls placed
            </div>
            <div className="mt-4 h-2 w-full rounded-full overflow-hidden" style={{ background: '#e6dcef' }}>
              <div
                className="h-full transition-all duration-500"
                style={{ width: `${pct}%`, background: 'var(--plum)' }}
                data-testid="daily-progress-bar"
              />
            </div>
            <div className="mt-2 text-xs" style={{ color: 'var(--muted)' }}>
              {pct}% of your daily goal
            </div>
          </div>
          <button
            className="hq-btn-plum inline-flex items-center gap-2 shrink-0"
            onClick={startNextCall}
            data-testid="start-calling-btn"
          >
            <PlayCircle size={16} /> Start Calling
          </button>
        </div>
      </div>

      {/* Stats grid */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={Target} label="Planned Today" value={summary?.daily_goal} hint="Daily goal" />
        <Stat icon={Phone} label="Placed Today" value={summary?.sessions_today} />
        <Stat icon={CheckCircle2} label="Conversations" value={summary?.conversations_today} tint="var(--success)" />
        <Stat icon={Bell} label="Follow-ups Due" value={summary?.followups_due} tint="var(--gold-soft)" />
        <Stat icon={Users} label="Pipeline Leads" value={summary?.pipeline_leads} />
        <Stat icon={CheckCircle2} label="Appointments Today" value={summary?.appointments_today} tint="var(--success)" />
        <Stat icon={Flame} label="Hot Leads" value={summary?.hot_leads} tint="var(--danger)" />
        <Stat icon={ShieldOff} label="DNC Records" value={summary?.dnc_count} tint="var(--muted)" />
      </section>

      {/* Quick nav cards */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickCard to="/call/leads" icon={Users} title="Lead Lists" body="Import CSVs, add leads, manage campaigns, flag DNC." testId="quick-leads" />
        <QuickCard to="/call/practice" icon={GraduationCap} title="Practice Mode" body="Role-play with an AI lead. Get a fair, kind score." testId="quick-practice" />
        <QuickCard to="/call/analytics" icon={BarChart3} title="Analytics" body="Dials, conversation rate, calls per appointment." testId="quick-analytics" />
      </section>

      {/* Compliance & Ethics reminder */}
      <div className="hq-card p-6" data-testid="compliance-notice">
        <div className="flex items-start gap-4">
          <ShieldOff size={18} className="mt-1 shrink-0" style={{ color: 'var(--plum)' }} />
          <div className="text-sm" style={{ color: 'var(--ink-soft)', lineHeight: 1.7 }}>
            <div className="hq-plum-eyebrow mb-2">Compliance First</div>
            Homi Call Command is <strong>human-led</strong>. You place every call. Homi never auto-dials,
            drops prerecorded voicemails, or pretends to be human. Any opt-out (spoken, tapped, or logged)
            adds the number to your internal DNC list <em>permanently</em> — re-import cannot bypass it.
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickCard({ to, icon: Icon, title, body, testId }) {
  return (
    <Link
      to={to}
      className="hq-card p-6 block hover:-translate-y-0.5 transition-transform"
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-3">
        <Icon size={20} style={{ color: 'var(--plum)' }} strokeWidth={1.4} />
        <ArrowRight size={14} style={{ color: 'var(--muted)' }} />
      </div>
      <div className="font-serif text-2xl" style={{ color: 'var(--sage)' }}>{title}</div>
      <div className="text-sm mt-2" style={{ color: 'var(--ink-soft)', lineHeight: 1.6 }}>{body}</div>
    </Link>
  );
}
